// Auto-tour confirmation. Surfaces AFTER a drag/assign creates or breaks
// same-vehicle adjacency — nothing happens to a tour without the producer
// confirming here. Drives the existing /tours endpoints through the store.

import React, { useRef, useState } from 'react';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import { useToast } from '../components/Toast/ToastContext';
import { Button } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';

const TourProposalModal: React.FC = () => {
  const proposal = useVehicleScheduleStore(s => s.tourProposal);
  const clear = useVehicleScheduleStore(s => s.clearTourProposal);
  const createTourFromEvents = useVehicleScheduleStore(s => s.createTourFromEvents);
  const extendTour = useVehicleScheduleStore(s => s.extendTour);
  const removeEventFromTour = useVehicleScheduleStore(s => s.removeEventFromTour);
  const dayMode = useThemeStore(s => s.dayMode);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // The store clears the proposal the moment it is answered; the dialog keeps the last one so its exit has words.
  const last = useRef(proposal);
  if (proposal) last.current = proposal;
  const p = proposal ?? last.current;

  if (!p) return null;

  const run = async (fn: () => Promise<{ ok: boolean; error?: string }>, ok: string) => {
    setBusy(true);
    const res = await fn();
    setBusy(false);
    clear();
    if (res.ok) toast.success(ok);
    else toast.error(res.error || 'Tour change failed');
  };

  let title = '';
  let body: React.ReactNode = null;
  let confirmLabel = 'Confirm';
  let onConfirm: () => void = clear;
  let danger = false;

  if (p.kind === 'create') {
    title = 'Create a tour?';
    confirmLabel = 'Create tour';
    body = (
      <>
        <p className="text-sm text-on-surface day:text-gray-700">These shows are now back-to-back on the same vehicle. Group them into a new tour?</p>
        <ul className="mt-2 space-y-1">
          {p.eventNames.map((n, i) => (
            <li key={i} className="text-sm text-on-surface day:text-gray-800">· {n}</li>
          ))}
        </ul>
        <p className="text-xs text-on-surface-variant day:text-gray-500 mt-2">The tour uses the first show's leave date and the last show's return date.</p>
      </>
    );
    onConfirm = () => run(() => createTourFromEvents(p.eventIds), 'Tour created');
  } else if (p.kind === 'extend') {
    title = `Add to ${p.tourName}?`;
    confirmLabel = 'Add to tour';
    body = (
      <>
        <p className="text-sm text-on-surface day:text-gray-700">
          {p.eventNames.length === 1 ? 'This show is' : 'These shows are'} now adjacent to <span className="text-on-surface day:text-gray-900 font-medium">{p.tourName}</span>. Add {p.eventNames.length === 1 ? 'it' : 'them'} to that tour?
        </p>
        <ul className="mt-2 space-y-1">
          {p.eventNames.map((n, i) => (
            <li key={i} className="text-sm text-on-surface day:text-gray-800">· {n}</li>
          ))}
        </ul>
      </>
    );
    onConfirm = () => run(() => extendTour(p.tourId, p.eventIds), 'Added to tour');
  } else {
    title = p.willDissolve ? `Dissolve ${p.tourName}?` : `Remove from ${p.tourName}?`;
    confirmLabel = p.willDissolve ? 'Dissolve tour' : 'Remove from tour';
    danger = true;
    body = (
      <>
        <p className="text-sm text-on-surface day:text-gray-700">
          <span className="text-on-surface day:text-gray-900 font-medium">{p.eventName}</span> is no longer adjacent to the rest of <span className="text-on-surface day:text-gray-900 font-medium">{p.tourName}</span>. Remove it from the tour?
        </p>
        {p.willDissolve && (
          <p className="text-xs text-amber-400/90 day:text-amber-600 mt-2">
            This leaves fewer than two shows, so the tour will be dissolved. Both shows become standalone and any tour messages are deleted. The shows themselves are kept.
          </p>
        )}
      </>
    );
    onConfirm = () => run(() => removeEventFromTour(p.tourId, p.eventId), p.willDissolve ? 'Tour dissolved' : 'Removed from tour');
  }

  return (
    <Dialog
      open={!!proposal}
      onClose={clear}
      title={title}
      tone={danger ? 'danger' : 'default'}
      alert
      size="md"
      hideClose
      busy={busy}
      light={dayMode}
      footer={(
        <>
          <Button variant="quiet" onClick={clear} disabled={busy}>Not now</Button>
          <Button variant={danger ? 'danger' : 'filled'} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      )}
    >
      {/* The dialog is portalled out of the page's day-mode wrapper, so the body carries it itself. */}
      <div className={dayMode ? 'day-mode' : undefined}>{body}</div>
    </Dialog>
  );
};

export default TourProposalModal;
